import React from "react";
import { Input } from "./Input";
import { Select } from "./Select/Select";
import { SelectItems, SelectValue } from "../interfaces";
import {
  APP_ORDER_FEE_MAX,
  APP_SERVICE_FEE_PLACEHOLDER,
  AppServiceFeeType,
} from "../../../../hooks/useAppServiceFee";

/** The commission values an entity (client or concession) holds for app orders. */
export interface AppOrderFeeValues {
  app_service_fee: number | null;
  app_service_fee_type: AppServiceFeeType | null;
  app_service_fee_delivery: number | null;
  app_service_fee_delivery_type: AppServiceFeeType | null;
  app_delivery_free_threshold?: number | null;
}

export interface AppOrderFeeFieldsProps {
  disabled: boolean;
  fees: AppOrderFeeValues;
  /** Shows the subtotal at which seat delivery stops being charged for that entity's own items. */
  showFreeDeliveryThreshold?: boolean;
  emptyValueHint?: string;
  onChange: (fees: Partial<AppOrderFeeValues>) => void;
}

const FEE_TYPE_OPTIONS: SelectItems[] = [
  { name: "Monto fijo", path: String(AppServiceFeeType.Fixed) },
  { name: "Porcentual", path: String(AppServiceFeeType.Percentage) },
];

const FEE_TYPE_SELECT_VALUES: Record<AppServiceFeeType, SelectValue> = {
  [AppServiceFeeType.Fixed]: { label: "Monto fijo", value: String(AppServiceFeeType.Fixed) },
  [AppServiceFeeType.Percentage]: {
    label: "Porcentual",
    value: String(AppServiceFeeType.Percentage),
  },
};

/** Upper bound for percentage commissions (percent of the subtotal they apply over). */
const MAX_PERCENTAGE_VALUE: number = 100;

/** Maximum number of decimals accepted for commission inputs. */
const MAX_FEE_DECIMALS: number = 4;

/**
 * Parse a commission input: an empty value means not configured, and anything out of range or with
 * too many decimals is rejected so the field keeps its previous value.
 */
const parseAmountInput = (
  value: string,
  maxAmount: number,
): { valid: boolean; amount: number | null } => {
  if (value === "") {
    return { valid: true, amount: null };
  }

  const parsedAmount: number = Number(value);
  if (!Number.isFinite(parsedAmount) || parsedAmount < 0 || parsedAmount > maxAmount) {
    return { valid: false, amount: null };
  }

  const decimals: string = value.split(".")[1] ?? "";
  if (decimals.length > MAX_FEE_DECIMALS) {
    return { valid: false, amount: null };
  }

  return { valid: true, amount: parsedAmount };
};

interface FeeFieldProps {
  disabled: boolean;
  label: string;
  amount: number | null;
  type: AppServiceFeeType | null;
  onAmountChange: (amount: number | null) => void;
  onTypeChange: (type: AppServiceFeeType) => void;
}

/**
 * One commission: its amount and its fixed/percentage type. An empty amount means not configured,
 * so clearing the field also clears the type.
 */
const FeeField: React.FC<FeeFieldProps> = (props) => {
  const { disabled, label, amount, type, onAmountChange, onTypeChange } = props;
  const selectedType: AppServiceFeeType = type ?? AppServiceFeeType.Fixed;
  const isPercentage: boolean = selectedType === AppServiceFeeType.Percentage;
  const adornment: string = isPercentage ? "%" : "$";
  const maxAmount: number = isPercentage ? MAX_PERCENTAGE_VALUE : APP_ORDER_FEE_MAX;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Select
        label="Tipo de comisión"
        placeholder="Selecciona un tipo"
        disabled={disabled}
        isCopyable={disabled}
        items={FEE_TYPE_OPTIONS}
        value={FEE_TYPE_SELECT_VALUES[selectedType]}
        onChange={(option: SelectValue) =>
          onTypeChange(Number(option.value) as AppServiceFeeType)
        }
      />
      <Input
        type="number"
        inputMode="decimal"
        label={label}
        placeholder={APP_SERVICE_FEE_PLACEHOLDER}
        disabled={disabled}
        isCopyable={disabled}
        value={amount === null ? "" : String(amount)}
        onChange={(value: string) => {
          const parsed = parseAmountInput(value, maxAmount);
          if (parsed.valid) {
            onAmountChange(parsed.amount);
          }
        }}
        leftAdornment={<span className="text-gray-500">{adornment}</span>}
      />
    </div>
  );
};

/**
 * Commissions charged on app orders, stored as fields of the client or the concession. Left empty, the
 * entity charges nothing and — for a concession — the client's commission applies instead. The
 * free-delivery threshold waives the seat commission for the subtotal it applies over.
 */
export const AppOrderFeeFields: React.FC<AppOrderFeeFieldsProps> = (props) => {
  const { disabled, fees, showFreeDeliveryThreshold, emptyValueHint, onChange } = props;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-gray-900">
          Comisión de recolección en punto
        </h3>
        <FeeField
          disabled={disabled}
          label="Comisión"
          amount={fees.app_service_fee}
          type={fees.app_service_fee_type}
          onAmountChange={(amount) =>
            onChange({
              app_service_fee: amount,
              app_service_fee_type:
                amount === null ? null : (fees.app_service_fee_type ?? AppServiceFeeType.Fixed),
            })
          }
          onTypeChange={(type) => onChange({ app_service_fee_type: type })}
        />
      </div>
      <div className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-gray-900">
          Comisión de entrega a asiento
        </h3>
        <FeeField
          disabled={disabled}
          label="Comisión"
          amount={fees.app_service_fee_delivery}
          type={fees.app_service_fee_delivery_type}
          onAmountChange={(amount) =>
            onChange({
              app_service_fee_delivery: amount,
              app_service_fee_delivery_type:
                amount === null
                  ? null
                  : (fees.app_service_fee_delivery_type ??
                    AppServiceFeeType.Fixed),
            })
          }
          onTypeChange={(type) =>
            onChange({ app_service_fee_delivery_type: type })
          }
        />
        {showFreeDeliveryThreshold && (
          <Input
            type="number"
            inputMode="decimal"
            label="Monto mínimo para entrega en asiento gratis"
            placeholder={APP_SERVICE_FEE_PLACEHOLDER}
            disabled={disabled}
            isCopyable={disabled}
            value={
              fees.app_delivery_free_threshold === null ||
              fees.app_delivery_free_threshold === undefined
                ? ""
                : String(fees.app_delivery_free_threshold)
            }
            onChange={(value: string) => {
              const parsed = parseAmountInput(value, APP_ORDER_FEE_MAX);
              if (parsed.valid) {
                onChange({ app_delivery_free_threshold: parsed.amount });
              }
            }}
            leftAdornment={<span className="text-gray-500">$</span>}
          />
        )}
      </div>
      {emptyValueHint && <p className="text-xs text-gray-500">{emptyValueHint}</p>}
    </div>
  );
};
